// Shared content foundation for Skjeta.no's AI-generated content, replacing
// the old lore-core.ts (a fictional town's "lore bible"). Every generator
// (daily fact, news, city trivia, quiz) pulls its voice, source material,
// and - critically - its safety rules from here, so they stay consistent
// without duplicating the same instructions in five different prompt
// strings.
//
// The core shift: content used to be entirely invented (a fictional town,
// fictional people, fictional history), so nothing could be factually
// wrong about a real person or place. Real-world facts don't have that
// safety net - an AI asserting a false "fact" about a real, named person
// is a defamation risk, not just an embarrassing bug. PUBLIC_FIGURE_RULES
// below exists specifically to keep that risk down; nothing that touches a
// real named individual should skip it.

export const VOICE_GUIDE = `
Du skriver for Skjeta.no, et norsk humornettsted der alt innhold skal være
"skjeta" - grovt, skittent, på-kanten og litt ekkelt, på samme måte som en
god dritvits på et fylleparty. Dette er IKKE en nøytral fakta-side. Et
faktum uten en skitten, grov eller pinlig vri er ikke godt nok - finn
kroppsvæske-vinkelen, den pinlige historiske detaljen, den ekle
naturfakta-greia, eller den rått usexy sannheten i temaet, og skriv DEN.

Regelen som ALDRI vikes: selve faktumet må stemme. Det grove/skittne ligger
i HVILKET faktum du velger og HVORDAN du vinkler og formulerer det - aldri
i å dikte opp detaljer som ikke er sanne.

Skriv på vanlig norsk (bokmål), ikke tungt dialekt. Vær kort, konkret og
respektløst morsomt. Målet er noe folk deler fordi det er ekkelt-men-sant,
ikke noe en skolebok kunne trykket.
`.trim();

// A rotating pool of real countries/cities so prompts have concrete material
// to work from instead of the model picking the same handful every time.
export const COUNTRIES = [
  "Japan", "Brasil", "Finland", "Mongolia", "Kenya", "Sør-Korea", "Peru",
  "New Zealand", "Marokko", "Vietnam", "Skottland", "Island", "Chile",
  "Thailand", "Ungarn", "Namibia", "Filippinene", "Portugal", "Bhutan",
  "Canada", "India", "Estland", "Argentina", "Sør-Afrika", "Nederland",
  "Malaysia", "Georgia", "Uruguay", "Sveits", "Indonesia",
];

export const CITIES = [
  "Tokyo", "Reykjavik", "Ulaanbaatar", "Marrakech", "Bergen", "Cape Town",
  "Kyoto", "Wellington", "Tallinn", "Bangkok", "Valparaíso", "Nairobi",
  "Edinburgh", "Ljubljana", "Hanoi", "Bogotá", "Helsinki", "Busan",
  "Lisboa", "Chiang Mai",
];

export const FACT_CATEGORIES = [
  "Ekle, groteske eller pinlige historiske hygiene- og toalettvaner",
  "Skitne/rare lover om kropp, sex eller kroppsfunksjoner som faktisk finnes",
  "Frastøtende men ekte matkultur og lokale spesialiteter",
  "Rare og ekle parringsritualer eller kroppsfunksjoner i dyreriket",
  "Pinlige eller flaue historiske hendelser folk helst glemmer",
  "Kroppsvæsker, lukt og andre ufyselege detaljer fra historien eller vitenskapen",
  "Skamfulle eller drøye skikker knyttet til bryllup, begravelser eller religion",
  "Rare verdensrekorder innen noe ekkelt eller pinlig",
];

// Strict guardrail for any content that names a real, identifiable person.
// This is intentionally more restrictive than the general voice guide -
// the humor can live in the ANGLE and the writing, never in inventing or
// exaggerating a claim about what someone actually did, said, or is.
export const PUBLIC_FIGURE_RULES = `
Dette faktumet handler om en navngitt, virkelig person. Ekstra strenge regler gjelder:

1. Bruk KUN allment kjent, lett verifiserbar informasjon (fødselsdato/-sted,
   nasjonalitet, yrke/karriereprestasjoner, offentlig dokumenterte rekorder,
   velkjente og godt dokumenterte sitater).
2. ALDRI påstander om helse, kriminalitet, forhold/privatliv, politiske
   meninger, eller noe som helst du ikke er svært sikker på stemmer.
3. Ingen oppdiktede sitater, hendelser eller detaljer - dersom du er usikker
   på om noe faktisk stemmer, ikke bruk det. Velg heller en annen, sikrere
   vinkel på samme person.
4. Ikke gjett eller ekstrapoler ("han var sikkert..."). Bare det som faktisk
   er dokumentert.
5. Humoren skal ligge i VINKLINGEN og SKRIVEMÅTEN, ikke i å overdrive eller
   dikte opp noe om personen.

Er du i tvil om en påstand er sann - dropp den og velg et annet, tryggere faktum.
`.trim();

export function pickRandom<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}
